import React, { useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { vendorStatePanels } from '../../data/mockData';

const GEO_URL = '/india-states.topo.json';

const SHADES  = ['#DBEAFE', '#93C5FD', '#3B82F6', '#1D4ED8', '#1E3A8A'];
const NO_DATA = '#CBD5E1';

function getShade(value: number | undefined, min: number, max: number): string {
  if (value === undefined) return NO_DATA;
  const norm = (value - min) / (max - min || 1);
  return SHADES[Math.min(4, Math.floor(norm * 5))];
}

function darken(hex: string, factor = 0.82): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgb(${Math.round(r * factor)},${Math.round(g * factor)},${Math.round(b * factor)})`;
}

const VENDOR_STATES = new Set(vendorStatePanels.map(s => s.name));

interface VendorIndiaMapProps {
  onStateSelect?: (name: string | null) => void;
  selectedState?: string | null;
}

export default function VendorIndiaMap({ onStateSelect, selectedState }: VendorIndiaMapProps) {
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

  // Generation lookup (vendor states only)
  const genByName: Record<string, number> = {};
  vendorStatePanels.forEach(s => {
    const mwh = parseFloat(s.todayGeneration.replace(',', '').replace(' MWh', ''));
    genByName[s.name] = mwh;
  });

  const values = Object.values(genByName);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);

  const handleClick = (name: string) => {
    if (!VENDOR_STATES.has(name)) return;
    if (onStateSelect) {
      onStateSelect(selectedState === name ? null : name);
    }
  };

  return (
    <div className="relative w-full h-full select-none">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ center: [82.5, 22], scale: 900 }}
        style={{ width: '100%', height: '100%' }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map(geo => {
              const name  = geo.properties?.name as string;
              const gen   = genByName[name];
              const isVendor   = VENDOR_STATES.has(name);
              const isSelected = selectedState === name;
              const isHovered  = hoveredState === name;

              const fill = isVendor ? getShade(gen, minVal, maxVal) : NO_DATA;

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onClick={() => handleClick(name)}
                  onMouseEnter={e => {
                    if (!isVendor) return;
                    setHoveredState(name);
                    setTooltip({ x: e.clientX, y: e.clientY, text: `${name} · ${gen?.toLocaleString()} MWh` });
                  }}
                  onMouseMove={e => {
                    if (!isVendor) return;
                    setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null);
                  }}
                  onMouseLeave={() => {
                    setHoveredState(null);
                    setTooltip(null);
                  }}
                  style={{
                    default: {
                      fill: isSelected ? '#1D4ED8' : fill,
                      stroke: '#FFFFFF',
                      strokeWidth: 0.6,
                      outline: 'none',
                    },
                    hover: {
                      fill: isVendor ? (isSelected ? '#1E3A8A' : darken(fill)) : NO_DATA,
                      stroke: '#FFFFFF',
                      strokeWidth: isVendor ? 1 : 0.6,
                      outline: 'none',
                      cursor: isVendor ? 'pointer' : 'default',
                    },
                    pressed: { fill, stroke: '#FFFFFF', strokeWidth: 0.6, outline: 'none' },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {/* Hover tooltip */}
      {tooltip && (
        <div
          className="fixed z-50 bg-white border border-border rounded shadow-lg px-2.5 py-1.5 text-xs font-medium text-text-primary pointer-events-none"
          style={{ top: tooltip.y - 36, left: tooltip.x + 12 }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-2 left-2 bg-white/90 rounded-input px-2.5 py-1.5 text-xs space-y-1">
        <p className="text-text-secondary font-semibold mb-1">Generation today</p>
        <div className="flex items-center gap-1">
          {SHADES.map((s, i) => (
            <div key={i} className="w-5 h-2.5 rounded-sm" style={{ backgroundColor: s }} />
          ))}
          <span className="text-text-tertiary ml-1">High</span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <div className="w-5 h-2.5 rounded-sm" style={{ backgroundColor: NO_DATA }} />
          <span className="text-text-tertiary">No plants</span>
        </div>
      </div>
    </div>
  );
}
